import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle, Star, ShieldCheck, ArrowRight, Lightning, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import LifetimeBadge from "@/components/LifetimeBadge";
import { trackPurchase, trackInitiateCheckout } from "@/lib/fbPixel";

const FEATURES = [
  { label: "Unlimited AI proposal drafts (Claude Sonnet 4.5)", icon: Lightning },
  { label: "Live Grants.gov search with filters", icon: ArrowRight },
  { label: "Approval tracking & reporting dashboard", icon: ShieldCheck },
  { label: "Email-to-agency submissions + watermarked PDFs", icon: CheckCircle },
  { label: "Priority support via the Help assistant", icon: Star },
];

export default function Subscribe() {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const [subBusy, setSubBusy] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const cancelled = params.get("cancelled");
  const kind = params.get("kind");
  const [statusMsg, setStatusMsg] = useState("");
  const [subInfo, setSubInfo] = useState(null);
  const [receipts, setReceipts] = useState([]);
  // Guards against re-firing FB Pixel Purchase if the user reloads /subscribe?session_id=…
  const purchaseTrackedRef = useRef(false);

  useEffect(() => {
    if (cancelled) setStatusMsg("Payment cancelled.");
  }, [cancelled]);

  useEffect(() => {
    api.get("/subscription/status").then((r) => setSubInfo(r.data)).catch(() => {});
    api.get("/payments/receipts").then((r) => setReceipts(r.data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          const isSub = data.kind === "subscription" || kind === "subscription";
          // Fire Meta Pixel Purchase exactly once per successful checkout.
          if (!purchaseTrackedRef.current) {
            const value = (typeof data.amount_total === "number" ? data.amount_total : 0) / 100;
            trackPurchase({
              value: value > 0 ? value : undefined,
              currency: (data.currency || "usd").toUpperCase(),
              contentName: isSub ? "GrantFlow Subscription" : (data.package || "GrantFlow"),
              contentType: isSub ? "subscription" : "one_time",
            });
            purchaseTrackedRef.current = true;
          }
          setStatusMsg(isSub
            ? "Subscription active — auto-renews monthly!"
            : "Payment successful — premium activated!");
          toast.success(isSub ? "Subscribed. Welcome aboard." : "Premium activated.");
          await refresh();
          return;
        }
        if (data.status === "expired") {
          setStatusMsg("Payment session expired.");
          return;
        }
        if (attempts < 10) setTimeout(poll, 2000);
        else setStatusMsg("Still processing — your account will update once Stripe confirms.");
      } catch {
        setStatusMsg("Could not verify payment. Refresh in a minute.");
      }
    };
    setStatusMsg("Verifying payment…");
    poll();
  }, [sessionId, refresh, kind]);

  const startCheckout = async () => {
    setBusy(true);
    trackInitiateCheckout({ value: subInfo?.pass_usd });
    try {
      const { data } = await api.post("/payments/checkout", {
        package: "premium_pass",
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
    } finally {
      setBusy(false);
    }
  };

  const startLifetime = async () => {
    setBusy(true);
    trackInitiateCheckout({ value: subInfo?.lifetime_usd });
    try {
      const { data } = await api.post("/payments/checkout", {
        package: "lifetime",
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start lifetime checkout");
    } finally {
      setBusy(false);
    }
  };

  const startSubscription = async () => {
    setSubBusy(true);
    trackInitiateCheckout({ value: subInfo?.price_usd });
    try {
      const { data } = await api.post("/payments/subscription/create", {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start subscription");
    } finally {
      setSubBusy(false);
    }
  };

  const startFounderSubscription = async () => {
    setSubBusy(true);
    trackInitiateCheckout({ value: subInfo?.founder_price_usd });
    try {
      const { data } = await api.post("/payments/subscription/create-founder", {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start founder subscription");
    } finally {
      setSubBusy(false);
    }
  };

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const { data } = await api.get("/payments/portal");
      if (data.portal_url) {
        window.location.href = data.portal_url;
      } else {
        toast.message(data.note || "No active subscription to manage.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open portal");
    } finally {
      setPortalBusy(false);
    }
  };

  const isPremium = user?.is_premium;
  const isLifetime = user?.subscription_status === "lifetime";
  const status = user?.subscription_status;
  const days = user?.days_left ?? 0;
  const hasSub = !!subInfo?.has_stripe_customer && !!subInfo?.stripe_subscription_status;
  const subActive = subInfo?.stripe_subscription_status === "active" || subInfo?.stripe_subscription_status === "trialing";
  const cancelAtEnd = !!subInfo?.cancel_at_period_end;
  const isPaused = !!subInfo?.paused;
  const pauseResumesAt = subInfo?.pause_resumes_at;
  const founderEligible = !!subInfo?.founder?.eligible;
  const foundersRemaining = subInfo?.founder?.remaining ?? 0;
  const founderPrice = subInfo?.founder?.price_usd ?? 5.99;
  const isFounder = !!subInfo?.is_founder;

  return (
    <div className="max-w-5xl">
      <div className="gf-overline">Subscription</div>
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mt-2">
        Premium access.
      </h1>
      <p className="text-zinc-600 mt-3 max-w-2xl text-sm leading-relaxed">
        Choose monthly auto-renewal for hands-off access, or grab a single 30-day pass any time.
        Card data is handled by Stripe; we never store it.
      </p>

      {statusMsg && (
        <div data-testid="subscribe-status" className="mt-6 border border-blue-300 bg-blue-50 text-blue-900 px-4 py-3 text-sm">
          {statusMsg}
        </div>
      )}

      {founderEligible && (
        <div
          data-testid="founder-banner"
          className="mt-6 border-2 border-amber-500 bg-amber-50 px-5 py-4 flex items-start gap-4"
        >
          <Star size={28} className="text-amber-600 mt-0.5" weight="fill" />
          <div className="flex-1">
            <div className="gf-overline text-amber-700">Limited launch offer</div>
            <div className="font-display font-bold text-lg mt-1 tracking-tight">
              Founder's Plan — ${founderPrice.toFixed(2)}/month for life.
            </div>
            <p className="text-sm text-amber-900 mt-1">
              <b>{foundersRemaining}</b> of 100 slots remaining. Lock in 50% off forever — only
              for our first 100 customers. Auto-renews monthly; cancel anytime.
            </p>
          </div>
        </div>
      )}

      <div className={`grid ${founderEligible ? "md:grid-cols-4" : "md:grid-cols-3"} gap-6 mt-8`}>
        <div data-testid="current-plan" className="border border-zinc-200 bg-white p-6">
          <div className="gf-overline">Current plan</div>
          <div className="font-display font-bold text-2xl mt-2 capitalize tracking-tight flex items-center gap-2 flex-wrap">
            {isLifetime ? "Lifetime" : isFounder ? "Founder" : (status || "free")}
            <LifetimeBadge member={user} size="sm" />
          </div>
          <p className="text-sm text-zinc-600 mt-2">
            {isLifetime
              ? "Permanent premium access. Never expires. Thank you for backing GrantFlow ❤️"
              : isPremium
              ? `Active — ${days} day${days === 1 ? "" : "s"} remaining.`
              : status === "trial"
              ? `Trial active — ${days} day${days === 1 ? "" : "s"} remaining.`
              : "No active plan. Upgrade to keep generating proposals."}
          </p>
          {isFounder && (
            <div className="mt-3 inline-block text-[10px] px-2 py-1 bg-amber-100 text-amber-800 border border-amber-300 font-mono uppercase tracking-wider">
              Founder · ${founderPrice.toFixed(2)}/mo · locked
            </div>
          )}
          {hasSub && (
            <div className="mt-4 pt-4 border-t border-zinc-200 text-xs space-y-1">
              <div className="font-mono text-zinc-500">
                STRIPE: <span className={subActive ? "text-emerald-700" : "text-amber-700"}>
                  {subInfo.stripe_subscription_status}
                </span>
              </div>
              {isPaused && (
                <div
                  data-testid="paused-banner"
                  className="mt-2 px-2 py-1.5 bg-amber-50 border border-amber-200 text-amber-800"
                >
                  <div className="font-bold uppercase tracking-wider">Paused</div>
                  <div className="mt-0.5">
                    Billing is paused.
                    {pauseResumesAt && (
                      <> Resumes {new Date(pauseResumesAt).toLocaleDateString()}.</>
                    )}{" "}
                    Resume any time from the Stripe portal.
                  </div>
                </div>
              )}
              {cancelAtEnd && !isPaused && (
                <div className="text-amber-700">Auto-renew off — access ends on expiry.</div>
              )}
              <Button
                onClick={openPortal}
                disabled={portalBusy}
                variant="outline"
                size="sm"
                data-testid="portal-btn"
                className="gf-sharp mt-2 border-zinc-300 uppercase font-bold tracking-wide text-xs"
              >
                <ArrowsClockwise size={12} className="mr-1.5" />
                {portalBusy ? "Opening…" : isPaused ? "Resume / manage" : "Manage subscription"}
              </Button>
            </div>
          )}
        </div>

        {founderEligible && (
          <div data-testid="founder-card" className="border-2 border-amber-500 bg-white p-6 relative">
            <div className="absolute -top-3 left-6 bg-amber-500 text-white text-[10px] font-bold tracking-wider px-2 py-0.5 uppercase">
              {foundersRemaining} left of 100
            </div>
            <div className="flex items-baseline justify-between">
              <div className="gf-overline text-amber-700">Founder's Plan</div>
              <div className="font-mono text-xs text-zinc-500">USD · for life</div>
            </div>
            <div className="font-display font-black text-5xl mt-2 tracking-tight text-amber-700">
              ${Math.floor(founderPrice)}.<span className="text-2xl">{String(founderPrice.toFixed(2)).split(".")[1]}</span>
              <span className="text-sm text-zinc-500 font-mono ml-1">/mo</span>
            </div>
            <div className="text-sm text-zinc-600 mt-1">
              Locked at this price forever. 50% off the standard plan.
            </div>
            <Button
              onClick={startFounderSubscription}
              disabled={subBusy || subActive}
              data-testid="founder-subscribe-btn"
              className="gf-sharp w-full mt-5 bg-amber-500 hover:bg-amber-600 text-white font-bold uppercase tracking-wide"
            >
              {subBusy ? "Redirecting…" : "Claim founder slot"}
            </Button>
            <div className="text-xs text-zinc-500 mt-3 font-mono">
              Auto-renews. Cancel anytime via portal.
            </div>
          </div>
        )}

        <div data-testid="subscription-card" className="border-2 border-blue-700 bg-white p-6 relative">
          <div className="absolute -top-3 left-6 bg-blue-700 text-white text-[10px] font-bold tracking-wider px-2 py-0.5 uppercase">
            Recommended
          </div>
          <div className="flex items-baseline justify-between">
            <div className="gf-overline text-blue-700">Monthly subscription</div>
            <div className="font-mono text-xs text-zinc-500">USD</div>
          </div>
          <div className="font-display font-black text-5xl mt-2 tracking-tight">
            $14.<span className="text-2xl">99</span>
            <span className="text-sm text-zinc-500 font-mono ml-1">/mo</span>
          </div>
          <div className="text-sm text-zinc-600 mt-1">Auto-renews each month — cancel anytime.</div>
          <Button
            onClick={startSubscription}
            disabled={subBusy || subActive}
            data-testid="subscribe-btn"
            className="gf-sharp w-full mt-5 bg-blue-700 hover:bg-blue-800 text-white font-bold uppercase tracking-wide"
          >
            {subActive ? "Already subscribed" : subBusy ? "Redirecting…" : "Subscribe monthly"}
          </Button>
          <div className="text-xs text-zinc-500 mt-3 font-mono">
            Manage or cancel via Stripe Customer Portal.
          </div>
        </div>

        <div data-testid="upgrade-card" className="border border-zinc-300 bg-white p-6">
          <div className="flex items-baseline justify-between">
            <div className="gf-overline">One-time pass</div>
            <div className="font-mono text-xs text-zinc-500">USD</div>
          </div>
          <div className="font-display font-black text-5xl mt-2 tracking-tight">
            $19.<span className="text-2xl">99</span>
          </div>
          <div className="text-sm text-zinc-600 mt-1">30 days, no auto-renewal.</div>
          <Button
            onClick={startCheckout}
            disabled={busy}
            variant="outline"
            data-testid="checkout-btn"
            className="gf-sharp w-full mt-5 border-zinc-400 uppercase font-bold tracking-wide"
          >
            {busy ? "Redirecting…" : isPremium ? "Extend by 30 days" : "Buy 30-day pass"}
          </Button>
          <div className="text-xs text-zinc-500 mt-3 font-mono">
            Stacks on top of your current window.
          </div>
        </div>

        <div data-testid="lifetime-card" className="border-2 border-zinc-900 bg-zinc-900 text-white p-6 relative">
          <div className="absolute -top-3 left-6 bg-amber-400 text-zinc-900 text-[10px] font-bold tracking-wider px-2 py-0.5 uppercase">
            Best value
          </div>
          <div className="flex items-baseline justify-between">
            <div className="text-[11px] font-mono uppercase tracking-widest text-amber-400">Lifetime</div>
            <div className="font-mono text-xs text-zinc-400">USD · once</div>
          </div>
          <div className="font-display font-black text-5xl mt-2 tracking-tight">
            <span className="text-2xl text-zinc-500 line-through font-bold mr-2">$199</span>
            ${(subInfo?.lifetime_usd ?? 77).toFixed(0)}
          </div>
          <div className="text-sm text-zinc-300 mt-1">Forever premium. No expiry.</div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-amber-400 mt-2" data-testid="lifetime-remaining">
            First 100 members only · {foundersRemaining} spot{foundersRemaining === 1 ? "" : "s"} left (shared with Founder)
          </div>
          <Button
            onClick={startLifetime}
            disabled={busy || isLifetime || (foundersRemaining <= 0 && !isLifetime)}
            data-testid="lifetime-btn"
            className="gf-sharp w-full mt-5 bg-amber-400 hover:bg-amber-500 text-zinc-900 font-bold uppercase tracking-wide disabled:opacity-60"
          >
            {isLifetime ? "Lifetime active ✓" : foundersRemaining <= 0 ? "Sold out" : busy ? "Redirecting…" : `Buy lifetime · $${(subInfo?.lifetime_usd ?? 77).toFixed(0)}`}
          </Button>
          <div className="text-xs text-zinc-400 mt-3 font-mono">
            One-time. Pays back in ~6 months vs monthly.
          </div>
        </div>
      </div>

      <div className="mt-10 border border-zinc-200 bg-white p-6 md:p-8">
        <div className="gf-overline">What you unlock</div>
        <ul className="mt-4 space-y-3">
          {FEATURES.map((f) => (
            <li key={f.label} className="flex items-start gap-3 text-sm">
              <f.icon size={20} className="text-blue-700 mt-0.5" />
              <span>{f.label}</span>
            </li>
          ))}
        </ul>
      </div>

      {receipts.length > 0 && (
        <div className="mt-6 border border-zinc-200 bg-white" data-testid="receipts-list">
          <div className="px-6 py-4 border-b border-zinc-200 flex items-baseline justify-between">
            <div>
              <div className="gf-overline">Receipts</div>
              <div className="font-display font-bold text-xl mt-1">Payment history</div>
            </div>
            <span className="font-mono text-xs text-zinc-500">{receipts.length} record{receipts.length === 1 ? "" : "s"}</span>
          </div>
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-zinc-200 text-left gf-overline">
                <th className="px-6 py-3">Receipt</th>
                <th className="px-6 py-3">Description</th>
                <th className="px-6 py-3">Amount</th>
                <th className="px-6 py-3">Paid</th>
                <th className="px-6 py-3">Email</th>
              </tr>
            </thead>
            <tbody>
              {receipts.map((r) => (
                <tr key={r.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`receipt-${r.id}`}>
                  <td className="px-6 py-3 font-mono text-xs">{r.receipt_number}</td>
                  <td className="px-6 py-3">{r.description}</td>
                  <td className="px-6 py-3 font-mono">${Number(r.amount_usd).toFixed(2)} {r.currency.toUpperCase()}</td>
                  <td className="px-6 py-3 text-zinc-500 font-mono text-xs">
                    {r.paid_at ? new Date(r.paid_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-6 py-3 text-xs">
                    {r.emailed
                      ? <span className="text-emerald-700">sent ✓</span>
                      : <span className="text-amber-700">recorded</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-6 text-xs text-zinc-500 font-mono">
        © {new Date().getFullYear()} GrantFlow Inc. All AI-generated content is
        the property of the requesting account holder; reproduction or scraping is prohibited.
      </div>
    </div>
  );
}
